import React, { useEffect, useState, useRef, useMemo } from 'react'
import { useLocation } from 'react-router'

import { useColResize } from '_hooks'
import {
  getEventsSchema,
  getEventFeed,
  updateEventFeed,
  deleteEventFeed,
} from '_services/data-explorer'
import User from '_lib/User.js'

import { useEventFeedsContext } from './context/index.jsx'
import { useNavigationContext } from '../../contexts/NavigationContext.jsx'
import { useRetractSidebar } from '_hooks'
import QueryPanel from './query-panel/index.jsx'
import VisualizationPanel from './visualization-panel/index.jsx'
import Header from './header/index.jsx'
import { logErrorAsync } from '../../lib/utility.js'
import { RouterPrompt } from '_components/router-prompt/index.jsx'
import { createFeed } from '_services/event-feeds'
import sendAnalytics from '_root/lib/sendAnalytics.js'
import EventFeedsWizard, { TYPE_PREBUILT_ATTRIBUTES_V2 } from './wizard/index.jsx'
import EnhancersModal from '_components/enhancers-modal/index.jsx'

import './styles.scss'

const MIN_QUERY_PANEL_WIDTH = 320
const MAX_QUERY_PANEL_WIDTH = 900

const INITIAL_DELIVERY_STATE = {
  frequency: null,
  sampleStartDate: null,
  sampleEndDate: null,
  format: null,
  compression: null,
  partitionType: null,
  destination: null,
  name: null,
  remoteS3Path: null,
  s3AccessKeyId: null,
  s3SecretAccessKey: null,
  num_files: 100,
  status: 'draft',
}

const ENTITY = 'event-feeds'

function useQuery() {
  const { search } = useLocation()

  return React.useMemo(() => new URLSearchParams(search), [search])
}

const EventFeeds = ({ match }) => {
  const query = useQuery()
  const { schema, dispatch, id, ACTIONS, data, permissions } = useEventFeedsContext()

  const [loading, setLoading] = useState({})
  const [notSaved, setNotSaved] = useState(false)
  const [wizardOpen, setWizardOpen] = useState(
    id === 'discovery' ? !JSON.parse(localStorage.getItem('do_not_show_wizard')) : false
  )
  const [enhancersOpen, setEnhancersOpen] = useState(false)

  const [queryOnboardShown, setQueryOnboardShown] = useState(() => {
    const localData = JSON.parse(localStorage.getItem('do_not_show_onboarding'))
    return !localData
  })
  const [visualizationOnboardShown, setVisualizationOnboardShown] = useState(false)
  const [headerOnboardShown, setHeaderOnboardShown] = useState(false)

  const containerRef = useRef(null)
  const firstDataEffect = useRef(true)

  const { setRemovePadding, setRemoveOverflow } = useNavigationContext()
  useRetractSidebar()

  useEffect(() => {
    // Apply { key: 'audience', operator: 'equals', value: 'audience id } filter if audience id is present in query params
    const audienceId = query.get('audience_id')
    const audienceName = query.get('audience_name')

    if (audienceId) {
      dispatch({
        type: ACTIONS.SET_DATA,
        payload: {
          ...data,
          filters: [
            {
              key: 'audience',
              operator: 'equals',
              value: audienceId,
            },
          ],
        },
      })

      if (audienceName) {
        localStorage.setItem(`@data-explorer/audience/${audienceId}/name`, audienceName)
      }
    }
  }, [])

  useEffect(() => {
    if (!firstDataEffect.current) {
      setNotSaved(true)
    } else {
      firstDataEffect.current = false
    }
  }, [data])

  useEffect(() => {
    setRemovePadding(true)
    setRemoveOverflow(true)

    return () => {
      setRemovePadding(false)
      setRemoveOverflow(false)
    }
  })

  useEffect(() => {
    const user = User.getProfile()

    if (user && !permissions) {
      if (!user.datasphere_permissions) {
        throw new Error(
          'You do not have the required permissions to access datasphere. Please contact support.'
        )
      }

      if (!user?.datasphere_permissions?.data_explorer) {
        throw new Error(
          'You do not have the required permissions to access data explorer. Please contact support.'
        )
      }

      dispatch({
        type: ACTIONS.SET_PERMISSIONS,
        payload: user.datasphere_permissions.data_explorer.permissions,
      })
    }
  })

  useEffect(() => {
    const getSchema = async () => {
      try {
        setLoading((prevState) => ({ ...prevState, isGetting: true }))
        const response = await getEventsSchema()

        dispatch({
          type: ACTIONS.SET_SCHEMA,
          payload: response,
        })
      } catch (error) {
        logErrorAsync(error)
        toast.error(error.message)
      }
      setLoading((prevState) => ({ ...prevState, isGetting: false }))
    }

    getSchema()
  }, [])

  const normalizedPrebuiltAttributes = useMemo(
    () =>
      schema &&
      Object.keys(schema.prebuilt_attributes_v2 || {})
        .filter(
          (key) =>
            typeof schema.prebuilt_attributes_v2[key] !== 'string' &&
            !Array.isArray(schema.prebuilt_attributes_v2[key])
        )

        .map((key) => {
          const val = schema.prebuilt_attributes_v2[key]
          return Object.keys(val)
        })
        .flat(),
    [schema]
  )

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading((prevState) => ({ ...prevState, isGetting: true }))
        const response = await getEventFeed(id)

        const predictors = response.type === TYPE_PREBUILT_ATTRIBUTES_V2

        dispatch({
          type: ACTIONS.SET_DATA,
          payload: {
            ...response,
            attributes: predictors
              ? response.attributes.filter((a) => normalizedPrebuiltAttributes.includes(a))
              : response.attributes,
            filters: predictors ? [] : response.filters,
            sources: response.sources || ['sharethis'],
            deliveries: response.deliveries?.[0] ? response.deliveries : [INITIAL_DELIVERY_STATE],
          },
        })
      } catch (error) {
        logErrorAsync(error)
        toast.error(error.message)
        setLoading((prevState) => ({ ...prevState, isGetting: false }))
      }
    }

    if (id !== 'discovery' && normalizedPrebuiltAttributes) getData()
  }, [normalizedPrebuiltAttributes])

  const handleSave = (creating, newFeedValues) => {
    return new Promise(async (resolve, reject) => {
      try {
        setLoading((prevState) => ({ ...prevState, isSaving: true }))
        setNotSaved(false)

        if (creating) {
          const newFeed = await createFeed(ENTITY, { ...data, ...newFeedValues })
          sendAnalytics('create', ENTITY, newFeed)
          window._history.push(`/data-explorer/${newFeed._id}`)
        } else {
          const response = await updateEventFeed(id, data)
          dispatch({
            type: ACTIONS.SET_DATA,
            payload: response,
          })
          toast('Successfully saved')
          resolve(response)
        }
      } catch (error) {
        logErrorAsync(error)
        toast.error(error.message)
        reject(error)
      }
      setLoading((prevState) => ({ ...prevState, isSaving: false }))
    })
  }

  const handleDelete = async () => {
    try {
      setLoading((prevState) => ({ ...prevState, isDeleting: true }))
      await deleteEventFeed(id)
      history.back()
      dispatch({
        type: ACTIONS.CLEAR_REDUCER,
      })
      toast('Successfully deleted')
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
    }
    setLoading((prevState) => ({ ...prevState, isDeleting: false }))
  }

  const applyWizardResult = (result) => {
    dispatch({
      type: ACTIONS.SET_DATA,
      payload: {
        ...data,
        filters: [...data.filters, ...result.filters],
        attributes: [...result.attributes],
      },
    })
  }

  const {
    Divider,
    handleMouseUp,
    handleMouseMove,
    handleOpenPanel,
    isResizing,
    handleClosePanel,
    panelWidth,
    isCollapsed,
  } = useColResize({
    min: MIN_QUERY_PANEL_WIDTH,
    max: MAX_QUERY_PANEL_WIDTH,
    containerRef,
  })

  return (
    <>
      <RouterPrompt
        when={id === 'discovery' && notSaved}
        title="Exit without saving?"
        description="The feed you configured will not be saved."
        cancelText="No"
        okText="Yes, exit"
        onOK={() => true}
        onCancel={() => false}
      />
      <div ref={containerRef} className="event-feeds__container">
        <Header
          onSave={handleSave}
          onDelete={handleDelete}
          loading={loading}
          onRunWizard={() => {
            setWizardOpen(true)
          }}
          panelWidth={panelWidth}
          headerOnboardShown={headerOnboardShown}
          onHeaderOnboardFinish={() => {
            setHeaderOnboardShown(false)
            localStorage.setItem('do_not_show_onboarding', 'true')
          }}
          onRewatchOnboardingTooltips={() => {
            if (!queryOnboardShown && !visualizationOnboardShown && !headerOnboardShown) {
              setQueryOnboardShown(true)
            }
          }}
          onEnhancersClick={() => setEnhancersOpen(true)}
        />
        <div
          className="event-feeds"
          style={{
            gridTemplateColumns: `${isCollapsed ? 60 : panelWidth}px 1px 1fr`,
            ...(isResizing ? { cursor: 'col-resize' } : {}),
          }}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
        >
          <QueryPanel
            onOpenPanel={handleOpenPanel}
            onClosePanel={handleClosePanel}
            className="event-feeds__query-panel"
            panelWidth={panelWidth}
            isCollapsed={isCollapsed}
            queryOnboardShown={queryOnboardShown}
            onQueryOnboardFinish={(viewedAllSteps) => {
              setQueryOnboardShown(false)
              if (viewedAllSteps) {
                setVisualizationOnboardShown(true)
              } else {
                localStorage.setItem('do_not_show_onboarding', 'true')
              }
            }}
          />
          <Divider className="event-feeds__divider" />
          <VisualizationPanel
            className="event-feeds__data-preview"
            match={match}
            visualizationOnboardShown={visualizationOnboardShown}
            panelWidth={panelWidth}
            onVisualizationOnboardFinish={(viewedAllSteps) => {
              setVisualizationOnboardShown(false)
              if (viewedAllSteps) {
                setHeaderOnboardShown(true)
              } else {
                localStorage.setItem('do_not_show_onboarding', 'true')
              }
            }}
          />
          <EventFeedsWizard
            namelessFeed
            isOpen={wizardOpen}
            onClose={() => setWizardOpen(false)}
            onFinish={applyWizardResult}
          />
          <EnhancersModal isOpen={enhancersOpen} onClose={() => setEnhancersOpen(false)} />
        </div>
      </div>
    </>
  )
}

export default EventFeeds
